import { useEnhancedMutation } from '@/modules/common/application';
import {
  ConfirmChatAttachmentUploadMutation,
  ConfirmChatAttachmentUploadMutationVariables,
  DiscardChatAttachmentUploadMutation,
  DiscardChatAttachmentUploadMutationVariables,
  PrepareChatAttachmentUploadMutation,
  PrepareChatAttachmentUploadMutationVariables,
} from '@/modules/common/types';

import {
  CONFIRM_CHAT_ATTACHMENT_UPLOAD,
  DISCARD_CHAT_ATTACHMENT_UPLOAD,
  PREPARE_CHAT_ATTACHMENT_UPLOAD,
} from '../apis/message.mutation';
import { getChatAttachmentContentType } from './chat-attachment-file-types';

export type PreparedChatAttachment = NonNullable<
  NonNullable<PrepareChatAttachmentUploadMutation['prepareChatAttachmentUpload']>['attachment']
>;
export type UploadedChatAttachment = NonNullable<
  ConfirmChatAttachmentUploadMutation['confirmChatAttachmentUpload']
>;

export class ChatAttachmentUploadError extends Error {
  attachment?: PreparedChatAttachment;

  constructor(message: string, options?: { attachment?: PreparedChatAttachment; cause?: unknown }) {
    super(message);
    this.name = 'ChatAttachmentUploadError';
    this.attachment = options?.attachment;
    this.cause = options?.cause;
  }
}

function getUploadErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Upload failed.';
}

export function useChatAttachmentUpload({
  conversationId,
  ensureConversationId,
  getConversationId,
  onAttachmentPrepared,
  projectId,
}: {
  conversationId: string | null | undefined;
  ensureConversationId?: () => Promise<string>;
  getConversationId?: () => string | null | undefined;
  onAttachmentPrepared?: (attachment: PreparedChatAttachment) => void;
  projectId: string | null | undefined;
}) {
  const [prepareChatAttachmentUpload, { loading: prepareChatAttachmentUploadLoading }] =
    useEnhancedMutation<
      PrepareChatAttachmentUploadMutation,
      PrepareChatAttachmentUploadMutationVariables
    >(PREPARE_CHAT_ATTACHMENT_UPLOAD);
  const [confirmChatAttachmentUpload, { loading: confirmChatAttachmentUploadLoading }] =
    useEnhancedMutation<
      ConfirmChatAttachmentUploadMutation,
      ConfirmChatAttachmentUploadMutationVariables
    >(CONFIRM_CHAT_ATTACHMENT_UPLOAD);
  const [discardChatAttachmentUpload, { loading: discardChatAttachmentUploadLoading }] =
    useEnhancedMutation<
      DiscardChatAttachmentUploadMutation,
      DiscardChatAttachmentUploadMutationVariables
    >(DISCARD_CHAT_ATTACHMENT_UPLOAD);

  const uploadFile = async (file: File) => {
    if (!projectId) {
      throw new Error('Project is required for chat attachment uploads.');
    }

    const resolvedConversationId =
      conversationId ?? getConversationId?.() ?? (await ensureConversationId?.());

    const contentType = getChatAttachmentContentType(file);

    if (!contentType) {
      throw new Error('Unsupported file type.');
    }

    let attachment: PreparedChatAttachment | undefined;

    try {
      const prepared = await prepareChatAttachmentUpload({
        variables: {
          input: {
            conversationId: resolvedConversationId ?? null,
            projectId,
            fileName: file.name,
            contentType,
            sizeBytes: file.size,
          },
        },
      });
      const upload = prepared.data?.prepareChatAttachmentUpload.upload;
      attachment = prepared.data?.prepareChatAttachmentUpload.attachment;

      if (!upload || !attachment) {
        throw new Error('Could not prepare chat attachment upload.');
      }

      onAttachmentPrepared?.(attachment);

      if (upload.method !== 'PUT') {
        throw new Error(`Unsupported upload method: ${upload.method}`);
      }

      const response = await fetch(upload.url, {
        method: upload.method,
        headers: upload.headers as HeadersInit,
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}.`);
      }

      const confirmed = await confirmChatAttachmentUpload({
        variables: {
          input: {
            id: attachment.id,
            projectId,
          },
        },
      });

      if (!confirmed.data?.confirmChatAttachmentUpload) {
        throw new Error('Could not confirm chat attachment upload.');
      }

      return confirmed.data.confirmChatAttachmentUpload;
    } catch (error) {
      throw new ChatAttachmentUploadError(getUploadErrorMessage(error), {
        attachment,
        cause: error,
      });
    }
  };

  const discardAttachment = async (attachmentId: string) => {
    if (!projectId) {
      throw new Error('Project is required for chat attachment uploads.');
    }

    await discardChatAttachmentUpload({
      variables: {
        input: {
          id: attachmentId,
          projectId,
        },
      },
    });
  };

  return {
    discardAttachment,
    uploadFile,
    uploadLoading:
      prepareChatAttachmentUploadLoading ||
      confirmChatAttachmentUploadLoading ||
      discardChatAttachmentUploadLoading,
  };
}
